import { APIError, AgentOutboundCallResult, AgentTextStartRequest, AgentTextStartResult, BotMessageDeliveryStatus } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

/**
 * A voice action the bot refused with a reason (a 4xx `APIError`: someone already listening, the
 * call already over) keeps that status and message on the way through the server, so Studio can
 * show it instead of "the call may have ended" (ENG-3807). Anything else stays a plain failure.
 */
async function relayVoiceAction<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (error) {
    const status = (error as { status?: number }).status;
    if (error instanceof Error && typeof status === "number" && status >= 400 && status < 500) {
      throw new APIError(error.message, status);
    }
    throw error;
  }
}

export interface PushMessageResponse {
  success: boolean;
  /** What the carrier did with a text (ENG-3281): sent, simulated, blocked or failed; absent on web. */
  deliveryStatus?: BotMessageDeliveryStatus;
  /** The gate's reason or a send error code when not sent (a code, never the carrier's message). */
  reason?: string;
  delivered: boolean;
  error?: string;
}

export class ContactCenterRelayAPI {
  /**
   * Push an agent message to a customer through the bot server's active connection.
   * The message should already be saved to bot_message before calling this.
   *
   * `isEvent=true` marks the message as a takeover-event ("You are now chatting
   * with X") so the widget renders it as a centered system notice instead of an
   * agent reply bubble. Per docs/snowy/widget/2026-05-20-widget-p1-plan.md §P1c.
   */
  static async pushMessage(
    botConversationId: number,
    content: string,
    agentName: string,
    agentMemberId: number,
    isEvent: boolean = false,
    /**
     * Identity of the already-saved bot_message row (ENG-2825). The relay
     * carries it onto the wire so every surface sees the SAME messageId +
     * messageIndex the row has — no re-derived index, no fabricated id.
     */
    message?: { messageId: string; messageIndex: number },
    /** Public S3 URL of an image attachment (ENG-2828) — inline structured payload on web, Twilio MMS mediaUrl on SMS. */
    imageUrl?: string,
    /** Structured payload for web delivery (ENG-2957: agent-sent unit cards). Already saved on the row. */
    structured?: { kind: string; data: unknown; onSelect?: { kind: "send-message" | "noop"; message?: string } },
    /** Our number a text reply goes out from, chosen by the CC server (ENG-3279 decision 3); the bot checks it is the org's. */
    fromPhoneNumber?: string
  ): Promise<PushMessageResponse> {
    return APIService.Bot.post<PushMessageResponse>("/contact-center-relay/push-message", {
      botConversationId,
      content,
      agentName,
      agentMemberId,
      isEvent,
      message,
      imageUrl,
      structured,
      fromPhoneNumber
    });
  }

  // ─── Coaching (ENG-2957) ─────────────────────────────────────────────────

  /** Server → bot: broadcast a coaching event to the agent conversation room + live-agents room. */
  static async coachingBroadcast(orgId: number, botConversationId: number, event: string, payload: unknown): Promise<{ success: boolean }> {
    return APIService.Bot.post<{ success: boolean }>("/contact-center-relay/coaching-broadcast", { orgId, botConversationId, event, payload });
  }

  /** Server → bot: an escalation was just accepted — flip the engine to live phase and tick immediately. */
  static async coachingEscalationStarted(botConversationId: number, orgId: number): Promise<{ ok: boolean }> {
    return APIService.Bot.post<{ ok: boolean }>("/contact-center-relay/coaching/escalation-started", { botConversationId, orgId });
  }

  /**
   * Server → bot: the agent executed a card action / changed focus (ENG-3051)
   * — nudge-lane-only re-tick, no score/summary, no phase flip.
   */
  static async coachingAgentAction(botConversationId: number, orgId: number): Promise<{ ok: boolean }> {
    return APIService.Bot.post<{ ok: boolean }>("/contact-center-relay/coaching/agent-action", { botConversationId, orgId });
  }

  /** Server → bot: generate the close-time summary for a conversation the CC just closed (fire-and-forget on the bot). */
  static async generateSummary(botConversationId: number): Promise<{ ok: boolean }> {
    return APIService.Bot.post<{ ok: boolean }>("/contact-center-relay/generate-summary", { botConversationId });
  }

  /**
   * Server → bot: proactive takeover on a FINALIZED thread (ENG-3036) — the
   * bot creates the fresh conversation the takeover will own and rebinds any
   * live widget socket from the dead thread to it.
   */
  static async freshConversationForTakeover(
    orgId: number,
    userId: number,
    channel: "web" | "sms",
    fromBotConversationId: number
  ): Promise<{ botConversationId: number }> {
    return APIService.Bot.post<{ botConversationId: number }>("/contact-center-relay/cc/fresh-conversation", {
      orgId,
      userId,
      channel,
      fromBotConversationId
    });
  }

  /** Create-Ticket LLM prefill: draft title/description/tags from the conversation (review-and-edit; creates nothing). */
  /** Agent toolbar (§13.7): create an agent-selected nudge card on demand. */
  static async coachingAgentNudge(botConversationId: number, type: string, param?: string, memberId?: number): Promise<{ nudgeId: number | null }> {
    return APIService.Bot.post("/contact-center-relay/coaching/agent-nudge", { botConversationId, type, param, memberId });
  }

  /** Policy nudge on-expand draft (§13.6) — the message the agent reviews/edits. */
  static async coachingPolicyDraft(botConversationId: number, policyKey: string, agentSelected?: boolean): Promise<{ message: string }> {
    return APIService.Bot.post("/contact-center-relay/coaching/policy-draft", { botConversationId, policyKey, agentSelected });
  }

  static async coachingTicketPrefill(botConversationId: number): Promise<{ title: string; description: string; tags: string[] }> {
    return APIService.Bot.post("/contact-center-relay/coaching/ticket-prefill", { botConversationId });
  }

  /** Voice "Text it": SMS the caller mid-call; recorded in the SMS thread + a voice-conversation event row. */
  static async textDuringCall(botConversationId: number, content: string, agentName: string, agentMemberId: number): Promise<PushMessageResponse> {
    return APIService.Bot.post<PushMessageResponse>("/contact-center-relay/coaching/text-during-call", {
      botConversationId,
      content,
      agentName,
      agentMemberId
    });
  }

  /** New Conversation (ENG-3592): start an agent's 1:1 text through the priming function. */
  static async startAgentText(body: AgentTextStartRequest): Promise<AgentTextStartResult> {
    return APIService.Bot.post<AgentTextStartResult>("/contact-center-relay/cc/start-agent-text", body);
  }

  // ─── Typing ──────────────────────────────────────────────────────────────

  static async pushTyping(botConversationId: number, isTyping: boolean): Promise<{ success: boolean }> {
    return APIService.Bot.post<{ success: boolean }>("/contact-center-relay/typing", { botConversationId, isTyping });
  }

  // ─── Voice escalation push notifications ─────────────────────────────────

  /** Server → bot: an agent accepted the assignment for this conversation. Bot stops hold state. */
  static async notifyAssignmentAccepted(botConversationId: number): Promise<{ ok: boolean }> {
    return APIService.Bot.post<{ ok: boolean }>(`/contact-center-relay/conversation/${botConversationId}/assignment-accepted`, {});
  }

  /** Server → bot: routing exhausted, no agent available. Bot speaks fallback + hangs up. */
  static async notifyEscalationFallthrough(botConversationId: number): Promise<{ ok: boolean }> {
    return APIService.Bot.post<{ ok: boolean }>(`/contact-center-relay/conversation/${botConversationId}/escalation-fallthrough`, {});
  }

  /**
   * Server → bot: the agent completed a voice assignment. A caller still held
   * for a bridge that never happened is released to the bot (ENG-3408); after a
   * real bridge, or with no hold, the bot ignores it.
   */
  static async notifyEscalationReleased(botConversationId: number): Promise<{ ok: boolean }> {
    return APIService.Bot.post<{ ok: boolean }>(`/contact-center-relay/conversation/${botConversationId}/escalation-released`, {});
  }

  // ─── Online Status ───────────────────────────────────────────────────────

  static async getOnlineUsers(orgId: number): Promise<{ userId: number; channel: string; viewing: boolean }[]> {
    return APIService.Bot.get<{ userId: number; channel: string; viewing: boolean }[]>(`/contact-center-relay/online-users/${orgId}`);
  }

  // ─── Voice ──────────────────────────────────────────────────────────────

  static async generateVoiceToken(
    orgId: number,
    memberId: number,
    accountCreds?: { accountSid: string; apiKey: string; apiSecret: string },
    sessionId?: string
  ): Promise<{ token: string; identity: string }> {
    return APIService.Bot.post<{ token: string; identity: string }>("/voice/token", { orgId, memberId, accountCreds, sessionId });
  }

  static async listenIn(botConversationId: number, orgId: number, twilioToken: string): Promise<{ roomName: string }> {
    return relayVoiceAction(() => APIService.Bot.post<{ roomName: string }>(`/voice/listen/${botConversationId}`, { orgId, twilioToken }));
  }

  static async bargeIn(botConversationId: number, orgId: number, twilioToken: string): Promise<{ success: boolean }> {
    return relayVoiceAction(() => APIService.Bot.post<{ success: boolean }>(`/voice/barge/${botConversationId}`, { orgId, twilioToken }));
  }

  static async bridgeToAgent(botConversationId: number, orgId: number, twilioToken: string): Promise<{ success: boolean }> {
    return relayVoiceAction(() => APIService.Bot.post<{ success: boolean }>(`/voice/bridge/${botConversationId}`, { orgId, twilioToken }));
  }

  /**
   * Server → bot: hang up the member's own human-agent leg (ENG-3448). Bridged: that leg drops and
   * the call ends only if no other agent remains (ENG-3821). Ringing: the leg is cancelled.
   */
  static async hangupAgentLeg(botConversationId: number, memberId: number): Promise<{ ended: boolean }> {
    return relayVoiceAction(() => APIService.Bot.post<{ ended: boolean }>(`/voice/hangup-agent/${botConversationId}`, { memberId }));
  }

  static async initiateOutboundCall(
    orgId: number,
    memberId: number,
    toNumber: string,
    fromNumber?: string,
    twilioToken?: string,
    consentOverride?: boolean
  ): Promise<AgentOutboundCallResult> {
    return APIService.Bot.post<AgentOutboundCallResult>("/voice/call", {
      orgId,
      memberId,
      toNumber,
      fromNumber,
      twilioToken,
      consentOverride
    });
  }

  static async getParticipants(botConversationId: number): Promise<{ label: string; callSid: string; muted: boolean }[]> {
    return APIService.Bot.get<{ label: string; callSid: string; muted: boolean }[]>(`/voice/participants/${botConversationId}`);
  }
}
